import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import {
  buildImportRequests,
  DiscSelection,
  discPickState,
  formatDicomDate,
  ImportRequest,
  MedicalDisc,
  PickState,
  setDiscSelected,
  setStudySelected,
  summarizeSelection,
  SelectionSummary,
} from './medical-images.util';

/**
 * "Medical Images" view of the Clio transfer dialog: lists the DICOM discs found in the matter or folder and the
 * studies inside each disc. Ticking a disc imports it as a whole DISC; ticking some studies imports only those.
 * The parent owns the import (it creates the medicalDisc jobs); this component only collects the choice.
 */
@Component({
  selector: 'app-medical-images-panel',
  templateUrl: './medical-images-panel.component.html',
  styleUrls: ['./medical-images-panel.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MedicalImagesPanelComponent implements OnChanges {
  @Input() discs: MedicalDisc[] = [];
  @Input() loading = false;
  @Input() error = '';
  @Input() truncated = false;
  /** Text typed in the dialog's search box. */
  @Input() filter = '';
  /** Folder id of a disc to open (set by the View studies link of the file list). */
  @Input() focusFolderId = '';
  @Output() selectionChange = new EventEmitter<{ requests: ImportRequest[]; summary: SelectionSummary }>();
  @Output() retryRequested = new EventEmitter<void>();

  selection: DiscSelection = {};
  expanded: { [folderId: string]: boolean } = {};

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.focusFolderId && this.focusFolderId) {
      this.expanded[this.focusFolderId] = true;
    }
  }

  get summary(): SelectionSummary {
    return summarizeSelection(this.discs, this.selection);
  }

  pickState(disc: MedicalDisc): PickState {
    return discPickState(disc, this.selection);
  }

  isStudyChecked(disc: MedicalDisc, studyUID: string): boolean {
    return !!(this.selection[disc.folderId] || {})[studyUID];
  }

  toggleExpanded(disc: MedicalDisc): void {
    this.expanded[disc.folderId] = !this.expanded[disc.folderId];
  }

  /** Discs that match the search text (name, folder path or any study). */
  get visibleDiscs(): MedicalDisc[] {
    const q = (this.filter || '').trim().toLowerCase();
    if (!q) {
      return this.discs;
    }
    return this.discs.filter(
      disc =>
        `${disc.name} ${disc.path}`.toLowerCase().includes(q) ||
        disc.studies.some(s => `${s.studyDescription} ${s.patientName} ${s.modalities.join(' ')}`.toLowerCase().includes(q)),
    );
  }

  onDiscChecked(disc: MedicalDisc, checked: boolean): void {
    this.selection = setDiscSelected(disc, this.selection, checked);
    this.emitSelection();
  }

  onStudyChecked(disc: MedicalDisc, studyUID: string, checked: boolean): void {
    this.selection = setStudySelected(disc, this.selection, studyUID, checked);
    this.emitSelection();
  }

  private emitSelection(): void {
    this.selectionChange.emit({
      requests: buildImportRequests(this.discs, this.selection),
      summary: summarizeSelection(this.discs, this.selection),
    });
  }

  formatDate(value: string): string {
    return formatDicomDate(value);
  }

  clearSelection(): void {
    this.selection = {};
    this.emitSelection();
  }

  trackDisc(_: number, disc: MedicalDisc): string {
    return disc.folderId;
  }

  trackStudy(_: number, study: { studyUID: string }): string {
    return study.studyUID;
  }
}
